import { AnalyticsService } from '@alison/analytics/client';
import { Button, brand, radius } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ASK_ALISON_THINKING_FRAMES } from '../../assets/ask-alison-thinking.js';
import { dateWindowFromRange } from '../../lib/dateUtils.js';
import { isForbidden, useRunResult, useStartRun } from '../../lib/workflows.hooks.js';
import type { ScopeState } from '../filters/types.js';
import { useScopeAccounts } from '../filters/useScopeSelection.js';
import { AskAlisonThinkingIcon } from './AskAlisonThinkingIcon.js';
import { parseAskEvo } from './askEvoResult.js';
import { ResultView } from './ResultView.js';
import type { WorkflowRun } from './workflows.types.js';

const MAX_TRACKED_QUESTION = 500;
const CONVERSATION_MAX_HEIGHT = 350;

const CARD_SX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${radius.xl}px`,
  bgcolor: 'background.paper',
  p: 3,
} as const;

const FOLLOW_UP_CHIP_SX = {
  height: 24,
  borderRadius: `${radius.md}px`,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: brand.purple,
  color: brand.purple,
  fontSize: 10,
} as const;

const TURN_ENTER_SX = {
  animation: 'askAlisonTurnEnter 200ms ease',
  '@keyframes askAlisonTurnEnter': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
} as const;

interface Turn {
  question: string;
  runId: string;
}

type RunPoll = { data: WorkflowRun | undefined; error: unknown };

function QuestionBubble({ question }: { question: string }): ReactNode {
  return (
    <Box
      sx={{
        alignSelf: 'flex-start',
        maxWidth: '80%',
        px: 3,
        py: 2,
        borderRadius: '12px 12px 0 12px',
        bgcolor: brand.bgBlue,
      }}
    >
      <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{question}</Typography>
    </Box>
  );
}

function ThinkingRow(): ReactNode {
  const { providerName } = useTenant();
  return (
    <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
      <AskAlisonThinkingIcon />
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
        {providerName} is thinking…
      </Typography>
    </Stack>
  );
}

function turnRecord(run: RunPoll): WorkflowRun | null | undefined {
  return run.data ?? (isForbidden(run.error) ? null : undefined);
}

function TurnExchange({
  turn,
  record,
}: {
  turn: Turn;
  record: WorkflowRun | null | undefined;
}): ReactNode {
  return (
    <>
      <QuestionBubble question={turn.question} />
      {record === undefined || record?.status === 'running' ? (
        <ThinkingRow />
      ) : record === null || record.status === 'failed' ? (
        <Alert severity="error">This question could not be answered.</Alert>
      ) : (
        <ResultView run={record} />
      )}
    </>
  );
}

function HistoricalTurn({ turn }: { turn: Turn }): ReactNode {
  const run = useRunResult(turn.runId);

  return (
    <Stack sx={{ ...CARD_SX, ...TURN_ENTER_SX, gap: 3 }}>
      <TurnExchange turn={turn} record={turnRecord(run)} />
    </Stack>
  );
}

export function AskAlisonBar({ scope }: { scope: ScopeState }): ReactNode {
  const { providerName } = useTenant();
  const [inputValue, setInputValue] = useState('');
  const [previewValue, setPreviewValue] = useState<string | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const { accountIds } = useScopeAccounts(scope);
  const startRun = useStartRun();
  const dateWindow = useMemo(() => dateWindowFromRange(scope.range, new Date()), [scope.range]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const olderTurns = turns.slice(0, -1);
  const latestTurn = turns.length === 0 ? null : (turns[turns.length - 1] ?? null);
  const latestRun = useRunResult(latestTurn?.runId ?? null);
  const latestRecord = turnRecord(latestRun);
  const isAnswerPending =
    startRun.isPending ||
    (latestTurn !== null && (latestRecord === undefined || latestRecord?.status === 'running'));
  const latestFollowUps =
    latestRecord?.status === 'completed'
      ? (parseAskEvo(latestRecord.result)?.follow_ups ?? [])
      : [];

  useEffect(() => {
    if (turns.length === 0) return;
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [turns.length]);

  function ask(question: string, source: 'input' | 'follow-up'): void {
    const trimmed = question.trim();
    if (trimmed === '' || isAnswerPending) return;
    AnalyticsService.track('Ask Alison - Ask', {
      source,
      question: trimmed.slice(0, MAX_TRACKED_QUESTION),
    });
    startRun.mutate(
      {
        actionType: 'askEvo',
        scope: {
          dateRange: dateWindow,
          ...(accountIds.length > 0 ? { accountIds } : {}),
        },
        params: { question: trimmed },
      },
      {
        onSuccess: ({ runId }) => {
          setTurns((current) => [...current, { question: trimmed, runId }]);
          setInputValue('');
        },
        onError: () => {
          AnalyticsService.track('Ask Alison - Ask Failed');
        },
      },
    );
  }

  const startRunErrorMessage = startRun.isError
    ? startRun.error instanceof Error
      ? startRun.error.message
      : `Something went wrong asking ${providerName}.`
    : null;

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter') return;
    ask(inputValue, 'input');
  }

  return (
    <Stack sx={{ ...CARD_SX, gap: 3, borderColor: turns.length === 0 ? 'transparent' : 'divider' }}>
      {turns.length === 0 ? null : (
        <Stack sx={{ gap: 3, maxHeight: CONVERSATION_MAX_HEIGHT, overflowY: 'auto' }}>
          {olderTurns.map((turn) => (
            <HistoricalTurn key={turn.runId} turn={turn} />
          ))}
          {latestTurn === null ? null : <TurnExchange turn={latestTurn} record={latestRecord} />}
          <div ref={bottomRef} />
        </Stack>
      )}

      <Stack
        direction="row"
        sx={{
          gap: 1,
          alignItems: 'center',
          border: '1px solid',
          borderColor: isInputFocused ? 'primary.main' : 'divider',
          borderRadius: `${radius.md}px`,
          transition: 'border-color 0.15s ease',
          px: 1,
          py: 1,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {startRun.isPending ? (
            <AskAlisonThinkingIcon />
          ) : (
            <Box
              component="img"
              src={ASK_ALISON_THINKING_FRAMES[0]}
              alt=""
              sx={{ width: 32, height: 32 }}
            />
          )}
        </Box>
        <TextField
          fullWidth
          variant="standard"
          value={previewValue ?? inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={onInputKeyDown}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          disabled={isAnswerPending}
          placeholder={
            turns.length === 0
              ? `Ask ${providerName} what's working, what's wasted, or what's next…`
              : `Ask a follow-up or start a new topic with ${providerName}`
          }
          slotProps={{ input: { disableUnderline: true } }}
          sx={{
            '& .MuiInputBase-input': { fontSize: 13 },
            '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 },
          }}
        />
        <Button
          variant="gradient"
          disabled={inputValue.trim() === '' || isAnswerPending}
          onClick={() => ask(inputValue, 'input')}
          endIcon={<ArrowForwardOutlined sx={{ fontSize: 10 }} />}
          sx={{
            flexShrink: 0,
            borderRadius: `${radius.pill}px`,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Ask {providerName}
        </Button>
      </Stack>

      {latestFollowUps.length === 0 ? null : (
        <Stack direction="row" sx={{ gap: 1.5, flexWrap: 'wrap' }}>
          {latestFollowUps.map((followUp) => (
            <Chip
              key={followUp}
              size="small"
              label={followUp}
              disabled={isAnswerPending}
              onMouseEnter={() => setPreviewValue(followUp)}
              onMouseLeave={() => setPreviewValue(null)}
              onClick={() => ask(followUp, 'follow-up')}
              sx={FOLLOW_UP_CHIP_SX}
            />
          ))}
        </Stack>
      )}

      {startRunErrorMessage === null ? null : (
        <Alert severity="error">{startRunErrorMessage}</Alert>
      )}
    </Stack>
  );
}
